import _ from 'lodash'
import moment, { type MomentInput } from 'moment'
import { type IDateRange } from '../interfaces/ejenta.interface'
import { type IMemberSummary } from '../interfaces/member-summary.interface'
import { type IMember } from '../interfaces/user.interface'

export const BASE_RANGE_VIEWS = {
  ONE_DAY: 'P1D',
  THREE_DAYS: 'P3D',
  WEEK: 'P7D',
  TWO_WEEKS: 'P14D',
  THREE_WEEKS: 'P21D',
  MONTH: 'P1M',
  THREE_MONTHS: 'P3M',
  SIX_MONTHS: 'P6M',
  YEAR: 'P1Y'
}

export const PREG_RANGE_VIEWS = _.merge(BASE_RANGE_VIEWS, {
  WEEK_CONDENSED: 'week-condensed',
  TRIMESTER: 'trimester',
  PREGNANCY: 'pregnancy'
})

export const formatDuration = (duration: moment.Duration): string => {
  // Example formatting (1 week, 2 weeks, 1 month, 3 months, 1 year)
  return duration.humanize({ d: 7, w: 4 }).replace('a ', '1 ') // change 'a week' to '1 week'
}

export const getDeliveryDate = (user: IMember): moment.Moment => {
  const conceptionDate = moment(user.conception_date)
  const deliveryDate = conceptionDate.add(40, 'weeks')

  return deliveryDate
}

export const getWeekOfPregnancy = (member: IMemberSummary, date: string | number): number => {
  const conceptionDate = moment(member.user.conception_date)
  let pregnancyWeek = moment(date).diff(conceptionDate, 'weeks')

  if (isNaN(pregnancyWeek)) {
    // this can happen when conceptionDate and date are the same day
    pregnancyWeek = 0
  }

  return pregnancyWeek
}

export const getTrimesterOfPregnancy = (member: IMemberSummary, date?: moment.MomentInput): number => {
  if (!date) return member.pregnancy_info?.current_trimester ?? 1
  if (moment(date).isBefore(moment(member.pregnancy_info?.trimester_2.start_date))) return 1
  if (moment(date).isBefore(moment(member.pregnancy_info?.trimester_3.start_date))) return 2
  return 3
}

export const getTrimesterBounds = (member: IMemberSummary, trimester: number): IDateRange => {
  if (!member.pregnancy_info) return { startDate: '', endDate: '' }

  if (trimester === 1) {
    return {
      startDate: member.pregnancy_info.trimester_1.start_date,
      endDate: moment(member.pregnancy_info.trimester_2.start_date).subtract(1, 'days')
    }
  }

  if (trimester === 2) {
    return {
      startDate: member.pregnancy_info.trimester_2.start_date,
      endDate: moment(member.pregnancy_info.trimester_3.start_date).subtract(1, 'days')
    }
  }

  return {
    startDate: member.pregnancy_info.trimester_3.start_date,
    endDate: moment()
  }
}

export const getPregRangeBounds = (member: IMemberSummary, rangeZoom: string, rangeStart: MomentInput): IDateRange => {
  if (!member) return { startDate: '', endDate: '' }

  let trimesterBounds: IDateRange
  let startDate: MomentInput = ''
  let endDate: MomentInput = ''

  switch (rangeZoom) {
    // Single week
    case PREG_RANGE_VIEWS.WEEK:
      if (!rangeStart) {
        startDate = moment().subtract(6, 'days')
        endDate = moment()
      } else {
        startDate = rangeStart
        endDate = moment(rangeStart).add(6, 'days')
      }

      break

    // Single trimester
    case PREG_RANGE_VIEWS.TRIMESTER:
      trimesterBounds = getTrimesterBounds(
        member,
        getTrimesterOfPregnancy(member, rangeStart)
      )
      startDate = trimesterBounds.startDate
      endDate = trimesterBounds.endDate
      break

    // Full pregnancy
    case PREG_RANGE_VIEWS.PREGNANCY:
      startDate = moment(member.user.conception_date)
      endDate = moment()
      break

    default:
      break
  }

  const momentFormat = (m: moment.MomentInput): string => moment(m).format('YYYY-MM-DD')
  return { startDate: momentFormat(startDate), endDate: momentFormat(endDate) }
}
